import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Grid,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PieChart } from '@mui/x-charts/PieChart';
import { UtilityService, CommissionPayment, Supplier } from '../../../types/utility.types';
import api from '../../../utils/api';

interface CommissionTrackerProps {
  filters?: {
    startDate?: Date | null;
    endDate?: Date | null;
    serviceTypes: string[];
  };
}

interface CommissionSummary {
  totalExpected: number;
  totalReceived: number;
  outstanding: number;
  contractCount: number;
  subBrokerContracts: number;
  subBrokerCommission: number;
  statusBreakdown: {
    notStarted: number;
    partial: number;
    paid: number;
    overpaid: number;
  };
}

type SortField = 'clientName' | 'serviceType' | 'contractValue' | 'totalCommissionReceived' | 'outstandingBalance' | 'expectedPaymentDate' | 'subBroker';
type SortOrder = 'asc' | 'desc';

const CommissionTracker: React.FC<CommissionTrackerProps> = ({ filters }) => {
  const [contracts, setContracts] = useState<UtilityService[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('clientName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  useEffect(() => {
    fetchCommissionData();
  }, [filters]);

  const fetchCommissionData = async () => {
    try {
      setLoading(true);
      // F-201 (Audit 19): canonical axios client — token attach + 401 refresh
      // handled by interceptors instead of a manual localStorage read.
      const response = await api.get('/utility-services');
      const data = response.data;

      // Ensure data is an array
      if (!Array.isArray(data)) {
        console.error('Expected array but got:', data);
        setContracts([]);
        return;
      }

      // Filter only Live contracts and exclude N/A commission status.
      // F-201: the dashboard filters genuinely constrain the population —
      // serviceTypes match the contract's serviceType, and the date window
      // selects contracts whose term overlaps it (services without a
      // recorded term can't be shown to be in period and are excluded).
      const liveContracts = data.filter((contract: UtilityService) => {
        const isLive = contract.status === 'Live';
        const hasCommissionValue = (contract as any).commissionStatus !== 'N/A';
        if (!isLive || !hasCommissionValue) return false;

        if (filters?.serviceTypes?.length &&
            !filters.serviceTypes.includes(contract.serviceType)) {
          return false;
        }

        if (filters?.startDate && filters?.endDate) {
          if (!contract.contractStartDate || !contract.contractEndDate) return false;
          const start = new Date(contract.contractStartDate);
          const end = new Date(contract.contractEndDate);
          if (end < filters.startDate || start > filters.endDate) return false;
        }

        return true;
      });

      setContracts(liveContracts);
    } catch (error) {
      console.error('Error fetching commission data:', error);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (): CommissionSummary => {
    const summary: CommissionSummary = {
      totalExpected: 0,
      totalReceived: 0,
      outstanding: 0,
      contractCount: contracts.length,
      subBrokerContracts: 0,
      subBrokerCommission: 0,
      statusBreakdown: {
        notStarted: 0,
        partial: 0,
        paid: 0,
        overpaid: 0
      }
    };

    contracts.forEach(contract => {
      const expected = Number(contract.contractValue) || 0;
      const received = Number(contract.totalCommissionReceived) || 0;
      
      summary.totalExpected += expected;
      summary.totalReceived += received;
      summary.outstanding += Number(contract.outstandingBalance) || 0;

      // Track sub-broker contracts and commission — prefer the locked
      // entitlement snapshot/virtual (Audit 03 invariant); formula fallback
      // for legacy contracts without a stored entitlement.
      if (contract.subBroker && contract.subBroker.userId) {
        summary.subBrokerContracts++;
        const subBrokerAmount = Number(contract.subBrokerEntitlement)
          || (expected * ((contract.subBroker.commissionRate || 0) / 100));
        summary.subBrokerCommission += subBrokerAmount;
      }

      // Count by status
      switch (contract.commissionStatus) {
        case 'Not Started':
          summary.statusBreakdown.notStarted++;
          break;
        case 'Partial':
          summary.statusBreakdown.partial++;
          break;
        case 'Paid':
          summary.statusBreakdown.paid++;
          break;
        case 'Overpaid':
          summary.statusBreakdown.overpaid++;
          break;
      }
    });

    return summary;
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  const calculateExpectedPaymentDate = (contract: UtilityService): Date | null => {
    if (!contract.contractStartDate) return null;
    
    const supplier = typeof contract.supplier === 'string' ? null : (contract.supplier as Supplier);
    if (!supplier?.paymentTerms) return null;

    const startDate = new Date(contract.contractStartDate);
    const { paymentFrequency, paymentDays } = supplier.paymentTerms;

    let expectedDate = new Date(startDate);

    // Add frequency period
    switch (paymentFrequency) {
      case 'UPFRONT':
        // Payment expected at start + payment days
        break;
      case 'MONTHLY':
        expectedDate.setMonth(expectedDate.getMonth() + 1);
        break;
      case 'QUARTERLY':
        expectedDate.setMonth(expectedDate.getMonth() + 3);
        break;
      case 'ANNUALLY':
        expectedDate.setFullYear(expectedDate.getFullYear() + 1);
        break;
    }

    // Add payment days
    expectedDate.setDate(expectedDate.getDate() + paymentDays);

    return expectedDate;
  };

  const getPaymentStatus = (contract: UtilityService): { status: string; color: 'success' | 'warning' | 'error' | 'info' } => {
    const expectedDate = calculateExpectedPaymentDate(contract);
    if (!expectedDate) return { status: 'Unknown', color: 'info' };

    const today = new Date();
    const daysUntil = Math.ceil((expectedDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    // If already paid in full
    if (contract.commissionStatus === 'Paid' || contract.commissionStatus === 'Overpaid') {
      return { status: 'Received', color: 'success' };
    }

    // If overdue
    if (daysUntil < 0) {
      return { status: `Overdue ${Math.abs(daysUntil)}d`, color: 'error' };
    }

    // If due soon (within 30 days)
    if (daysUntil <= 30) {
      return { status: `Due in ${daysUntil}d`, color: 'warning' };
    }

    // Future payment
    return { status: `Due in ${daysUntil}d`, color: 'info' };
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid':
        return 'success';
      case 'Partial':
        return 'warning';
      case 'Not Started':
        return 'error';
      case 'Overpaid':
        return 'info';
      case 'N/A':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const filteredAndSortedContracts = contracts
    .filter(contract => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      const supplierName = typeof contract.supplier === 'string' 
        ? contract.supplier 
        : (contract.supplier as any)?.name || '';
      return (
        contract.client?.companyName?.toLowerCase().includes(search) ||
        contract.serviceType?.toLowerCase().includes(search) ||
        supplierName.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'clientName':
          aValue = a.client?.companyName || '';
          bValue = b.client?.companyName || '';
          break;
        case 'serviceType':
          aValue = a.serviceType || '';
          bValue = b.serviceType || '';
          break;
        case 'contractValue':
          aValue = a.contractValue || 0;
          bValue = b.contractValue || 0;
          break;
        case 'totalCommissionReceived':
          aValue = a.totalCommissionReceived || 0;
          bValue = b.totalCommissionReceived || 0;
          break;
        case 'outstandingBalance':
          aValue = a.outstandingBalance || 0;
          bValue = b.outstandingBalance || 0;
          break;
        case 'expectedPaymentDate':
          const aDate = calculateExpectedPaymentDate(a);
          const bDate = calculateExpectedPaymentDate(b);
          aValue = aDate ? aDate.getTime() : 0;
          bValue = bDate ? bDate.getTime() : 0;
          break;
        case 'subBroker':
          aValue = a.subBroker?.userId ? '1' : '0'; // Has sub-broker vs doesn't
          bValue = b.subBroker?.userId ? '1' : '0';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortOrder === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });

  const summary = calculateSummary();

  const statusChartData = [
    { id: 0, value: summary.statusBreakdown.notStarted, label: 'Not Started', color: '#f44336' },
    { id: 1, value: summary.statusBreakdown.partial, label: 'Partial', color: '#ff9800' },
    { id: 2, value: summary.statusBreakdown.paid, label: 'Paid', color: '#4caf50' },
    { id: 3, value: summary.statusBreakdown.overpaid, label: 'Overpaid', color: '#2196f3' },
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(76, 175, 80, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Expected
              </Typography>
              <Typography variant="h4" sx={{ color: '#4caf50' }}>
                {formatCurrency(summary.totalExpected)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.contractCount} contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(33, 150, 243, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(33, 150, 243, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Total Received
              </Typography>
              <Typography variant="h4" sx={{ color: '#2196f3' }}>
                {formatCurrency(summary.totalReceived)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((summary.totalReceived / summary.totalExpected) * 100).toFixed(1)}% collected
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(255, 152, 0, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Outstanding
              </Typography>
              <Typography variant="h4" sx={{ color: '#ff9800' }}>
                {formatCurrency(summary.outstanding)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {((summary.outstanding / summary.totalExpected) * 100).toFixed(1)}% pending
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Status Breakdown
              </Typography>
              {statusChartData.length > 0 ? (
                <PieChart
                  series={[{
                    data: statusChartData,
                    innerRadius: 30,
                    outerRadius: 50,
                    paddingAngle: 2,
                    cornerRadius: 4,
                  }]}
                  height={120}
                  margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  slotProps={{
                    legend: { hidden: true }
                  }}
                  sx={{
                    '& .MuiPopper-root': {
                      '& .MuiChartsTooltip-root': {
                        background: 'rgba(30, 41, 59, 0.95) !important',
                        backdropFilter: 'blur(12px) !important',
                        WebkitBackdropFilter: 'blur(12px) !important',
                        border: '1px solid rgba(255, 255, 255, 0.1) !important',
                        borderRadius: '8px !important',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3) !important',
                      }
                    }
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">No data</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
            backdropFilter: 'blur(4px)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: 2
          }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Sub-Broker Deals
              </Typography>
              <Typography variant="h4" sx={{ color: '#9c27b0' }}>
                {formatCurrency(summary.subBrokerCommission)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.subBrokerContracts} contracts ({((summary.subBrokerContracts / summary.contractCount) * 100).toFixed(1)}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Table */}
      <Card sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(4px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2
      }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by client, service type, or supplier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'clientName'}
                      direction={sortField === 'clientName' ? sortOrder : 'asc'}
                      onClick={() => handleSort('clientName')}
                    >
                      Client
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'serviceType'}
                      direction={sortField === 'serviceType' ? sortOrder : 'asc'}
                      onClick={() => handleSort('serviceType')}
                    >
                      Service Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'contractValue'}
                      direction={sortField === 'contractValue' ? sortOrder : 'asc'}
                      onClick={() => handleSort('contractValue')}
                    >
                      Expected
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'totalCommissionReceived'}
                      direction={sortField === 'totalCommissionReceived' ? sortOrder : 'asc'}
                      onClick={() => handleSort('totalCommissionReceived')}
                    >
                      Received
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortField === 'outstandingBalance'}
                      direction={sortField === 'outstandingBalance' ? sortOrder : 'asc'}
                      onClick={() => handleSort('outstandingBalance')}
                    >
                      Outstanding
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'subBroker'}
                      direction={sortField === 'subBroker' ? sortOrder : 'asc'}
                      onClick={() => handleSort('subBroker')}
                    >
                      Sub-Broker
                    </TableSortLabel>
                  </TableCell>
                  <TableCell 
                    sortDirection={sortField === 'expectedPaymentDate' ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortField === 'expectedPaymentDate'}
                      direction={sortField === 'expectedPaymentDate' ? sortOrder : 'asc'}
                      onClick={() => handleSort('expectedPaymentDate')}
                    >
                      Expected Payment
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedContracts.map((contract) => (
                  <React.Fragment key={contract._id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => setExpandedRow(expandedRow === contract._id ? null : contract._id)}
                        >
                          {expandedRow === contract._id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{contract.client?.companyName || 'N/A'}</TableCell>
                      <TableCell>{contract.serviceType}</TableCell>
                      <TableCell align="right">{formatCurrency(Number(contract.contractValue) || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(contract.totalCommissionReceived || 0)}</TableCell>
                      <TableCell align="right">{formatCurrency(contract.outstandingBalance || 0)}</TableCell>
                      <TableCell>
                        <Chip 
                          label={contract.commissionStatus || 'N/A'} 
                          color={getStatusColor(contract.commissionStatus || 'N/A')}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {contract.subBroker && contract.subBroker.userId ? (
                          <Box>
                            <Chip 
                              label={contract.subBroker.level || 'Sub-Broker'}
                              color="secondary"
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption" display="block" color="text.secondary">
                              {contract.subBroker.commissionRate}% commission
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const expectedDate = calculateExpectedPaymentDate(contract);
                          const paymentStatus = getPaymentStatus(contract);
                          return (
                            <Box>
                              <Typography variant="body2">
                                {expectedDate ? expectedDate.toLocaleDateString('en-GB') : 'N/A'}
                              </Typography>
                              <Chip 
                                label={paymentStatus.status}
                                color={paymentStatus.color}
                                size="small"
                                sx={{ mt: 0.5 }}
                              />
                            </Box>
                          );
                        })()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedRow === contract._id} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              Payment History
                            </Typography>
                            {(contract as any).commissionPayments && (contract as any).commissionPayments.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Amount</TableCell>
                                    <TableCell>Payment Method</TableCell>
                                    <TableCell>Reference</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {((contract as any).commissionPayments as CommissionPayment[]).map((payment: CommissionPayment, idx: number) => (
                                    <TableRow key={idx}>
                                      <TableCell>
                                        {payment.dateReceived ? new Date(payment.dateReceived).toLocaleDateString('en-GB') : 
                                         payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('en-GB') : 'N/A'}
                                      </TableCell>
                                      <TableCell>{formatCurrency(payment.amount || 0)}</TableCell>
                                      <TableCell>{payment.paymentMethod || 'N/A'}</TableCell>
                                      <TableCell>{payment.reference || payment.notes || 'N/A'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No payment history available
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredAndSortedContracts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No LIVE contracts found
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CommissionTracker;
